import { ChevronDown, ChevronUp } from 'lucide-react'
import { forwardRef, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

export interface NumberInputProps extends Omit<NumericFormatProps, 'value' | 'onValueChange' | 'className'> {
  stepper?: number
  thousandSeparator?: string
  placeholder?: string
  defaultValue?: number
  min?: number
  max?: number
  value?: number // Controlled value
  suffix?: string
  prefix?: string
  onValueChange?: (value: number | undefined) => void
  fixedDecimalScale?: boolean
  decimalScale?: number
  // className applies to the wrapper container
  className?: string
  // inputClassName applies to the internal input element
  inputClassName?: string
  // error state for validation
  error?: boolean
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      stepper,
      thousandSeparator,
      placeholder,
      defaultValue,
      min = -Infinity,
      max = Infinity,
      onValueChange,
      fixedDecimalScale = false,
      decimalScale = 0,
      suffix,
      prefix,
      value: controlledValue,
      className: wrapperClassName,
      inputClassName,
      error,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState<number | undefined>(controlledValue ?? defaultValue)
    const inputRef = useRef<HTMLInputElement | null>(null)

    const setRefs = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el
        if (typeof ref === 'function') {
          ref(el)
        } else if (ref) {
          ;(ref as RefObject<HTMLInputElement | null>).current = el
        }
      },
      [ref]
    )

    const handleIncrement = useCallback(() => {
      setValue(prev => (prev === undefined ? (stepper ?? 1) : Math.min(prev + (stepper ?? 1), max)))
    }, [stepper, max])

    const handleDecrement = useCallback(() => {
      setValue(prev => (prev === undefined ? -(stepper ?? 1) : Math.max(prev - (stepper ?? 1), min)))
    }, [stepper, min])

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (document.activeElement === inputRef.current) {
          if (e.key === 'ArrowUp') {
            handleIncrement()
          } else if (e.key === 'ArrowDown') {
            handleDecrement()
          }
        }
      }

      window.addEventListener('keydown', handleKeyDown)

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }, [handleIncrement, handleDecrement])

    useEffect(() => {
      if (controlledValue !== undefined) {
        setValue(controlledValue)
      }
    }, [controlledValue])

    const handleChange = (values: { value: string; floatValue: number | undefined }) => {
      const newValue = values.floatValue === undefined ? undefined : values.floatValue
      setValue(newValue)
      if (onValueChange) {
        onValueChange(newValue)
      }
    }

    const handleBlur = () => {
      if (value !== undefined) {
        if (value < min) {
          setValue(min)
          if (inputRef.current) {
            inputRef.current.value = String(min)
          }
        } else if (value > max) {
          setValue(max)
          if (inputRef.current) {
            inputRef.current.value = String(max)
          }
        }
      }
    }

    return (
      <div className={`flex w-full min-w-0 flex-1 items-center ${wrapperClassName ?? ''}`}>
        <NumericFormat
          value={value}
          onValueChange={handleChange}
          thousandSeparator={thousandSeparator}
          decimalScale={decimalScale}
          fixedDecimalScale={fixedDecimalScale}
          allowNegative={min < 0}
          valueIsNumericString
          onBlur={handleBlur}
          max={max}
          min={min}
          suffix={suffix}
          prefix={prefix}
          customInput={Input}
          placeholder={placeholder}
          className={`relative w-full min-w-0 flex-1 [appearance:textfield] rounded-r-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${inputClassName ?? ''}`}
          getInputRef={setRefs}
          aria-invalid={error ? 'true' : undefined}
          {...props}
        />

        <div className={`flex flex-none flex-col ${error ? '[&>button]:border-destructive' : ''}`}>
          <Button
            type="button"
            aria-label="Increase value"
            className="border-input h-4.5 w-3 rounded-l-none rounded-br-none border-b border-l-0 px-1 focus-visible:relative"
            variant="outline"
            tabIndex={-1}
            onClick={handleIncrement}
            disabled={value === max}>
            <ChevronUp size={15} />
          </Button>
          <Button
            type="button"
            aria-label="Decrease value"
            className="border-input h-4.5 w-3 rounded-l-none rounded-tr-none border-t-[0.5px] border-l-0 px-2 focus-visible:relative"
            variant="outline"
            tabIndex={-1}
            onClick={handleDecrement}
            disabled={value === min}>
            <ChevronDown size={15} />
          </Button>
        </div>
      </div>
    )
  }
)
